import { IModel, IAttribute, IViolation, IValidationContext } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Dataset } from './dataset';

type TabularValidator = (dataset: TabularTypeDataset, value?: string, violation?: IViolation, context?: IValidationContext) => Promise<IViolation[] | undefined>;

interface ITabularDatasetValidators {
    validateTableName: TabularValidator;
    validateTable: TabularValidator;
    validateSchema: TabularValidator;
}

export const tabularDatasetValidators: ITabularDatasetValidators = {
    validateTableName: undefined,
    validateTable: undefined,
    validateSchema: undefined
};

@Metamodel.model({
    abstract: true
})
export abstract class TabularTypeDataset extends Dataset {
    public static readonly MODEL: IModel;
    public static readonly TABLE_NAME: IAttribute;
    public static readonly TABLE: IAttribute; // V2
    public static readonly SCHEMA: IAttribute; // V2

    @Metamodel.attribute({
        constraint: {
            validator: (dataset: TabularTypeDataset, tableName?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (tabularDatasetValidators.validateTableName) {
                    return tabularDatasetValidators.validateTableName(dataset, tableName, violation, context);
                }
                return undefined;
            }
        },
        association: 'TABLE_NAME',
        templatable: true
    })
    public tableName?: string;

    @Metamodel.attribute({
        association: 'TABLE',
        templatable: true,
        constraint: {
            validator: (dataset: TabularTypeDataset, table?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (tabularDatasetValidators.validateTable) {
                    return tabularDatasetValidators.validateTable(dataset, table, violation, context);
                }
                return undefined;
            }
        }
    })
    public table?: string;

    @Metamodel.attribute({
        association: 'SCHEMA',
        templatable: true,
        constraint: {
            validator: (dataset: TabularTypeDataset, schema?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (tabularDatasetValidators.validateSchema) {
                    return tabularDatasetValidators.validateSchema(dataset, schema, violation, context);
                }
                return undefined;
            }
        },
    })
    public schema?: string;

    public constructor(name?: string, description?: string) {
        super(name, description);
    }

    public override recoverDatasetConfiguration(
        dataset: Dataset,
        previousDataset: Dataset,
        config: { splitTablePath?: boolean } = { }
    ): void {
        if (!(dataset instanceof TabularTypeDataset) || !(previousDataset instanceof TabularTypeDataset)) {
            return;
        }

        const schemaAttribute = Metamodel.modelOf(dataset).attribute('schema');
        const tableAttribute = Metamodel.modelOf(dataset).attribute('table');
        const tableNameAttribute = Metamodel.modelOf(dataset).attribute('tableName');

        if (
            schemaAttribute.templateValue(dataset) != null ||
            schemaAttribute.value(dataset) != null ||
            tableAttribute.templateValue(dataset) != null ||
            tableAttribute.value(dataset) != null ||
            tableNameAttribute.templateValue(dataset) != null ||
            tableNameAttribute.value(dataset) != null
        ) {
            return;
        }

        if (config.splitTablePath) {
            schemaAttribute?.setTemplateValue(dataset, schemaAttribute.templateValue(previousDataset));
            tableAttribute?.setTemplateValue(dataset, tableAttribute.templateValue(previousDataset));
            schemaAttribute?.setValue(dataset, schemaAttribute.value(previousDataset));
            tableAttribute?.setValue(dataset, tableAttribute.value(previousDataset));
        } else {
            tableNameAttribute?.setTemplateValue(dataset, tableNameAttribute.templateValue(previousDataset));
            tableNameAttribute?.setValue(dataset, tableNameAttribute.value(previousDataset));
        }
    }
}
